import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  IconButton,
  Tooltip as MUITooltip
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon
} from "@material-ui/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const useStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
  },
  card: {
    height: "100%",
    borderRadius: 12,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8]
    }
  },
  cardContent: {
    padding: theme.spacing(2)
  },
  metricValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    marginBottom: theme.spacing(0.5)
  },
  metricLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary
  },
  table: {
    minWidth: 650
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-head": {
      color: theme.palette.primary.contrastText,
      fontWeight: 600
    }
  },
  chartContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200
  },
  errorContainer: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.error.main
  }
}));

const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  return new Intl.NumberFormat("es-ES").format(num);
};

const formatSize = (sizeMB) => {
  // Convertir a número si viene como string
  const size = typeof sizeMB === 'string' ? parseFloat(sizeMB) : (sizeMB || 0);
  
  if (!size || size === 0 || isNaN(size)) return "0 B";
  if (size < 1) {
    const kb = size * 1024;
    return `${kb.toFixed(2)} KB`;
  } else if (size < 1024) {
    return `${size.toFixed(2)} MB`;
  } else {
    return `${(size / 1024).toFixed(2)} GB`;
  }
};

const DatabaseActivityMetrics = ({ loading, error, data, onRefresh }) => {
  const classes = useStyles();

  if (loading && !data) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="body1">
          No hay datos disponibles. Haga clic en actualizar.
        </Typography>
      </Box>
    );
  }

  const { daily, historical, percentages, tableSizes } = data;

  // Preparar datos para gráfico de barras (diario) - formato recharts
  const dailyChartData = [
    { name: "Messages", value: daily.messages },
    { name: "Tickets", value: daily.tickets },
    { name: "Contacts", value: daily.contacts },
    { name: "GroupWhatsApp", value: daily.groupWhatsApp },
    { name: "QuickMessages", value: daily.quickMessages },
    { name: "UserRatings", value: daily.userRatings },
    { name: "GroupCampaigns", value: daily.groupCampaigns },
    { name: "GroupCampaignShippings", value: daily.groupCampaignShippings },
    { name: "GroupCampaignHourlySchedules", value: daily.groupCampaignHourlySchedules },
    { name: "GroupWhatsAppParticipants", value: daily.groupWhatsAppParticipants },
    { name: "Campaigns", value: daily.campaigns },
    { name: "CampaignShipping", value: daily.campaignShipping }
  ];

  // Preparar datos para gráfico de barras (histórico) - formato recharts
  const historicalChartData = [
    { name: "Messages", value: historical.messages },
    { name: "Tickets", value: historical.tickets },
    { name: "Contacts", value: historical.contacts },
    { name: "GroupWhatsApp", value: historical.groupWhatsApp },
    { name: "QuickMessages", value: historical.quickMessages },
    { name: "UserRatings", value: historical.userRatings },
    { name: "GroupCampaigns", value: historical.groupCampaigns },
    { name: "GroupCampaignShippings", value: historical.groupCampaignShippings },
    { name: "GroupCampaignHourlySchedules", value: historical.groupCampaignHourlySchedules },
    { name: "GroupWhatsAppParticipants", value: historical.groupWhatsAppParticipants },
    { name: "Campaigns", value: historical.campaigns },
    { name: "CampaignShipping", value: historical.campaignShipping }
  ];

  // Tabla comparativa
  const tableData = [
    { name: "Messages", daily: daily.messages, historical: historical.messages, dailyPct: percentages.daily.messages, historicalPct: percentages.historical.messages },
    { name: "Tickets", daily: daily.tickets, historical: historical.tickets, dailyPct: percentages.daily.tickets, historicalPct: percentages.historical.tickets },
    { name: "Contacts", daily: daily.contacts, historical: historical.contacts, dailyPct: percentages.daily.contacts, historicalPct: percentages.historical.contacts },
    { name: "GroupWhatsApp", daily: daily.groupWhatsApp, historical: historical.groupWhatsApp, dailyPct: percentages.daily.groupWhatsApp, historicalPct: percentages.historical.groupWhatsApp },
    { name: "QuickMessages", daily: daily.quickMessages, historical: historical.quickMessages, dailyPct: percentages.daily.quickMessages, historicalPct: percentages.historical.quickMessages },
    { name: "UserRatings", daily: daily.userRatings, historical: historical.userRatings, dailyPct: percentages.daily.userRatings, historicalPct: percentages.historical.userRatings },
    { name: "GroupCampaigns", daily: daily.groupCampaigns, historical: historical.groupCampaigns, dailyPct: percentages.daily.groupCampaigns, historicalPct: percentages.historical.groupCampaigns },
    { name: "GroupCampaignShippings", daily: daily.groupCampaignShippings, historical: historical.groupCampaignShippings, dailyPct: percentages.daily.groupCampaignShippings, historicalPct: percentages.historical.groupCampaignShippings },
    { name: "GroupCampaignHourlySchedules", daily: daily.groupCampaignHourlySchedules, historical: historical.groupCampaignHourlySchedules, dailyPct: percentages.daily.groupCampaignHourlySchedules, historicalPct: percentages.historical.groupCampaignHourlySchedules },
    { name: "GroupWhatsAppParticipants", daily: daily.groupWhatsAppParticipants, historical: historical.groupWhatsAppParticipants, dailyPct: percentages.daily.groupWhatsAppParticipants, historicalPct: percentages.historical.groupWhatsAppParticipants },
    { name: "Campaigns", daily: daily.campaigns, historical: historical.campaigns, dailyPct: percentages.daily.campaigns, historicalPct: percentages.historical.campaigns },
    { name: "CampaignShipping", daily: daily.campaignShipping, historical: historical.campaignShipping, dailyPct: percentages.daily.campaignShipping, historicalPct: percentages.historical.campaignShipping }
  ];

  return (
    <Box className={classes.section}>
      <Box className={classes.sectionTitle}>
        <Typography variant="h5" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AssessmentIcon />
          Actividad de Base de Datos
        </Typography>
        {onRefresh && (
          <MUITooltip title="Actualizar métricas">
            <IconButton onClick={onRefresh} color="primary" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </MUITooltip>
        )}
      </Box>

      {/* Cards de resumen */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Mensajes WhatsApp (24H)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(daily.messages)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {percentages.daily.messages.toFixed(2)}% del total diario
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Tickets (24H)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(daily.tickets)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {percentages.daily.tickets.toFixed(2)}% del total diario
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Contactos (24H)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(daily.contacts)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {percentages.daily.contacts.toFixed(2)}% del total diario
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Total Operaciones (24H)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(daily.total)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cards históricos */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Mensajes WhatsApp (Total)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(historical.messages)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {percentages.historical.messages.toFixed(2)}% del total histórico
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                {formatSize(tableSizes?.messages || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Tickets (Total)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(historical.tickets)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {percentages.historical.tickets.toFixed(2)}% del total histórico
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                {formatSize(tableSizes?.tickets || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Contactos (Total)
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(historical.contacts)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {percentages.historical.contacts.toFixed(2)}% del total histórico
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                {formatSize(tableSizes?.contacts || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography variant="body2" className={classes.metricLabel}>
                Total Acumulado
              </Typography>
              <Typography variant="h4" className={classes.metricValue}>
                {formatNumber(historical.total)}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                {formatSize(
                  (tableSizes?.messages || 0) +
                  (tableSizes?.tickets || 0) +
                  (tableSizes?.contacts || 0) +
                  (tableSizes?.groupWhatsApp || 0) +
                  (tableSizes?.quickMessages || 0) +
                  (tableSizes?.userRatings || 0) +
                  (tableSizes?.groupCampaigns || 0) +
                  (tableSizes?.groupCampaignShippings || 0) +
                  (tableSizes?.groupCampaignHourlySchedules || 0) +
                  (tableSizes?.groupWhatsAppParticipants || 0) +
                  (tableSizes?.campaigns || 0) +
                  (tableSizes?.campaignShipping || 0)
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos de barras */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actividad Diaria (24H)
              </Typography>
              <Box className={classes.chartContainer} style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis tickFormatter={(value) => formatNumber(value)} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend />
                    <Bar dataKey="value" fill="#2196f3" name="Actividad Diaria (24H)" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Histórico Acumulado
              </Typography>
              <Box className={classes.chartContainer} style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={historicalChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis tickFormatter={(value) => formatNumber(value)} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend />
                    <Bar dataKey="value" fill="#f44336" name="Total Histórico" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla comparativa */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tabla Comparativa
          </Typography>
          <TableContainer component={Paper}>
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>Tabla</TableCell>
                  <TableCell align="right">Diario (24H)</TableCell>
                  <TableCell align="right">% Diario</TableCell>
                  <TableCell align="right">Total Histórico</TableCell>
                  <TableCell align="right">% Histórico</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell align="right">{formatNumber(row.daily)}</TableCell>
                    <TableCell align="right">{row.dailyPct.toFixed(2)}%</TableCell>
                    <TableCell align="right">{formatNumber(row.historical)}</TableCell>
                    <TableCell align="right">{row.historicalPct.toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DatabaseActivityMetrics;
